import { useEffect, useMemo, useState } from 'react';
import type { MarketplaceFilters, MarketplaceProduct } from '@/types';
import { marketplaceService } from '@/services/marketplace/marketplaceService';

export const DEFAULT_FILTERS: MarketplaceFilters = {
  query: '',
  category: 'all',
  type: 'all',
  minPrice: null,
  maxPrice: null,
  sort: 'relevance',
  companyId: null,
};

/** Loads the catalog once and applies filters/sort client-side. */
export function useMarketplace(initial?: Partial<MarketplaceFilters>) {
  const [all, setAll] = useState<MarketplaceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<MarketplaceFilters>({ ...DEFAULT_FILTERS, ...initial });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const data = await marketplaceService.listAll();
      if (!cancelled) {
        setAll(data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(
    () => [...new Set(all.map((p) => p.category))].sort((a, b) => a.localeCompare(b)),
    [all],
  );
  const industries = useMemo(
    () => [...new Set(all.map((p) => p.industryId))].sort((a, b) => a.localeCompare(b)),
    [all],
  );

  const products = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    let list = all.filter((p) => {
      if (filters.companyId && p.companyId !== filters.companyId) return false;
      if (q && !(p.name.toLowerCase().includes(q) || p.companyName.toLowerCase().includes(q) || p.description.toLowerCase().includes(q))) return false;
      if (filters.category !== 'all' && p.category !== filters.category) return false;
      if (filters.type !== 'all' && p.type !== filters.type) return false;
      if (filters.minPrice != null && p.price < filters.minPrice) return false;
      if (filters.maxPrice != null && p.price > filters.maxPrice) return false;
      return true;
    });
    switch (filters.sort) {
      case 'price-asc': list = [...list].sort((a, b) => a.price - b.price); break;
      case 'price-desc': list = [...list].sort((a, b) => b.price - a.price); break;
      case 'rating': list = [...list].sort((a, b) => b.stats.averageRating - a.stats.averageRating); break;
      case 'newest': list = [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt)); break;
      default: list = [...list].sort((a, b) => b.stats.purchases - a.stats.purchases);
    }
    return list;
  }, [all, filters]);

  return { all, products, categories, industries, loading, filters, setFilters };
}
